<div class="dashboard-container">
  <!-- Toast for notifications -->
  <p-toast position="top-right" [life]="4000" role="status" aria-live="polite" aria-atomic="true"></p-toast>

  <!-- Breadcrumb navigation -->
  <nav aria-label="Navigazione breadcrumb">
    <p-breadcrumb
      [model]="breadcrumbItems()"
      [home]="{ icon: 'pi pi-home', routerLink: '/axa', ariaLabel: 'Home' }"
      styleClass="breadcrumb-nav"
    ></p-breadcrumb>
  </nav>

  <!-- Main content -->
  <main class="dashboard-content" role="main">
    <!-- Page header with accessibility -->
    <header class="page-header">
      <div class="header-content">
        <i [class]="'pi ' + tableIcon + ' header-icon'" aria-hidden="true"></i>
        <h1 id="page-title">{{ tableTitle }}</h1>
      </div>
      <p class="header-description">
        Visualizza e gestisci l'elenco completo dei fiduciari registrati. Utilizza i filtri per
        trovare rapidamente i fiduciari di interesse.
      </p>
    </header>

    <!-- Status Summary Cards -->
    <section class="status-cards-section" aria-labelledby="status-summary-title">
      <h2 id="status-summary-title" class="visually-hidden">Riepilogo per stato</h2>
      <div class="status-cards">
        @for (card of statusCards(); track card.status) {
          <!-- WCAG 4.1.2 + 2.1.1: Button nativo invece di div role="button" -->
          <button
            type="button"
            class="status-card"
            [class]="card.styleClass"
            (click)="filterByStatus(card.status)"
            [attr.aria-label]="card.label + ' ' + card.value + ' - Clicca per filtrare'"
          >
            <div class="card-label">{{ card.label | uppercase }}</div>
            <div class="card-value">{{ card.value.toLocaleString('it-IT') }}</div>
          </button>
        }
      </div>
    </section>

    <!-- Category Cards -->
    <section class="category-cards-section" aria-labelledby="category-summary-title">
      <h2 id="category-summary-title" class="visually-hidden">
        Riepilogo per categoria fiduciaria
      </h2>
      <div class="category-cards">
        @for (card of categoryCards(); track card.category) {
          <article class="category-card" [class]="card.colorClass">
            <div class="card-header">
              <h3 class="card-title">{{ card.label }}</h3>
              <!-- WCAG 4.1.2: Button nativo invece di div role="button" -->
              <button
                type="button"
                class="card-total"
                (click)="filterByCategoryAndStatus(card.category, 'ACTIVE')"
                [attr.aria-label]="'Totali attivi ' + card.active + ' - Filtra ' + card.label + ' per attivi'"
              >
                Totali Attivi {{ card.active.toLocaleString('it-IT') }}
              </button>
            </div>

            <div class="card-stats">
              <!-- WCAG 4.1.2: Button nativi invece di div role="button" -->
              <button
                type="button"
                class="stat-item in-progress"
                (click)="filterInProgressTable(card.category)"
                [attr.aria-label]="'In Lavorazione ' + card.inProgress + ' - Filtra'"
              >
                <span class="stat-label">In Lavorazione</span>
                <span class="stat-value">{{ card.inProgress }}</span>
              </button>

              <button
                type="button"
                class="stat-item waiting-response"
                (click)="filterByCategoryAndStatus(card.category, 'IN_ATTESA_DI_RISPOSTA')"
                [attr.aria-label]="'In Attesa di Risposta ' + card.waitingResponse + ' - Filtra'"
              >
                <span class="stat-label">In Attesa di Risposta</span>
                <span class="stat-value">{{ card.waitingResponse }}</span>
              </button>

              <button
                type="button"
                class="stat-item contratti"
                (click)="filterByCategoryAndStatus(card.category, 'ACTIVE', 'contracts')"
                [attr.aria-label]="'Contratti ' + card.contratti + ' - Filtra'"
              >
                <span class="stat-label">Contratti</span>
                <span class="stat-value">{{ card.contratti }}</span>
              </button>
            </div>

            <button
              pButton
              [label]="'Filtra per ' + card.label"
              [icon]="'pi pi-filter'"
              iconPos="right"
              [class]="'w-full ' + card.buttonClass"
              (click)="filterByCategory(card.category)"
              [attr.aria-label]="'Filtra per fiduciari ' + card.label"
              [attr.title]="'Filtra la tabella per categoria ' + card.label"
            ></button>
          </article>
        }
      </div>
    </section>

    <!-- Charts Section -->
    <section class="charts-section" aria-labelledby="charts-section-title">
      <h2 id="charts-section-title" class="visually-hidden">Analisi dati fiduciari</h2>
      <div class="charts-grid">
        <article class="chart-card">
          <header class="chart-header">
            <h3 class="chart-title" id="distribution-chart-title">Distribuzione per Tipologia Fiduciari</h3>
            <p class="chart-instructions">Questo grafico mostra la distribuzione percentuale dei fiduciari per tipologia. Clicca sulle sezioni per visualizzare i dettagli.</p>
          </header>
          <!-- WCAG 1.3.1: Chart con alternative testuale accessibile -->
          <div
            class="chart-content"
            role="img"
            aria-labelledby="distribution-chart-title"
            aria-describedby="distribution-chart-data"
          >
            @if (distributionChartData()) {
              <p-chart
                type="doughnut"
                [data]="distributionChartData()"
                [options]="distributionChartOptions()"
              ></p-chart>
            }
          </div>
          <!-- WCAG 1.3.1: Dati testuali per screen reader -->
          <div id="distribution-chart-data" class="visually-hidden">
            <p>Distribuzione fiduciari per tipologia:</p>
            @if (distributionChartData()) {
              <ul>
                @for (label of distributionChartData().labels; track $index; let i = $index) {
                  <li>
                    {{ label }}: 
                    {{ distributionChartData().datasets[0].data[i] }}
                    @if (getTotalDistribution() > 0) {
                      ({{ ((distributionChartData().datasets[0].data[i] / getTotalDistribution()) * 100).toFixed(1) }}%)
                    }
                  </li>
                }
              </ul>
            }
          </div>
        </article>

        <article class="chart-card">
          <header class="chart-header">
            <h3 class="chart-title" id="registrations-chart-title">Nuove Registrazioni per Tipologia Fiduciari</h3>
            <p class="chart-instructions">Questo grafico a barre mostra il numero di nuove registrazioni per ciascuna tipologia di fiduciario.</p>
          </header>
          <!-- WCAG 1.3.1: Chart con alternative testuale accessibile -->
          <div
            class="chart-content"
            role="img"
            aria-labelledby="registrations-chart-title"
            aria-describedby="registrations-chart-data"
          >
            @if (registrationsChartData()) {
              <p-chart
                type="bar"
                [data]="registrationsChartData()"
                [options]="registrationsChartOptions()"
              ></p-chart>
            }
          </div>
          <!-- WCAG 1.3.1: Dati testuali per screen reader -->
          <div id="registrations-chart-data" class="visually-hidden">
            <p>Nuove registrazioni per tipologia:</p>
            @if (registrationsChartData()) {
              <ul>
                @for (label of registrationsChartData().labels; track $index; let i = $index) {
                  <li>
                    {{ label }}: 
                    {{ registrationsChartData().datasets[0].data[i] }} nuove registrazioni
                  </li>
                }
              </ul>
            }
          </div>
        </article>
      </div>
    </section>

    <!-- WCAG 9.4.1.3: Status message region for filter results -->
    <div role="status" aria-live="polite" aria-atomic="true" class="visually-hidden">
      @if (data().length !== allData().length) {
        <p>Filtro applicato: {{ data().length }} di {{ allData().length }} fiduciari visualizzati</p>
      } @else {
        <p>Visualizzazione completa: {{ allData().length }} fiduciari totali</p>
      }
    </div>

    <!-- Registry Table Component -->
    <section #registryTableSection aria-labelledby="page-title">
      <app-registry-table
        [title]="tableTitle"
        [icon]="tableIcon"
        [columns]="columns"
        [data]="data()"
        [loading]="loading()"
        [showClearFiltersButton]="true"
        [emptyStateMessage]="tableEmptyStateMessage"
        [initialFilters]="tableInitialFilters()"
        [serverSideFilter]="true"
        [lockedFilterFields]="['professionalStatus']"
        (onDetailClick)="onDetailClick($event)"
        (onEditClick)="onEditClick($event)"
        (onFilterChange)="onTableFilterChange($event)"
        (onClearFilters)="onClearFilters()"
        aria-labelledby="page-title"
      ></app-registry-table>
    </section>
  </main>
</div>
